import { useState, useId, ChangeEvent, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Button } from "@mui/material";
import SvgIcon from "@mui/material/SvgIcon";
import type { UserCredential } from "firebase/auth";

import "./style.scss";
import { ReactComponent as EyeIcon } from "../../../assets/eye.icon.svg";
import { ReactComponent as EyeOffIcon } from "../../../assets/eye-off.icon.svg";
import { ReactComponent as GoogleIcon } from "../../../assets/google.icon.svg";
import { ReactComponent as MicrosoftIcon } from "../../../assets/microsoft.icon.svg";
import { ReactComponent as SsoIcon } from "../../../assets/fingerprint.icon.svg";
import { AuthErrorMessage } from "../../../common/enums/auth-error-message.enum";
import { FirebaseErrorCode } from "../../../common/enums/firebase-error-code.enum";
import type { SsoProviderOption } from "../../../common/interfaces/admin/tenant.interface";
import { useTenantAuthOptions } from "../../../common/utilities/tenant-auth/use-tenant-auth-options.util";
import { passwordMeetsRules } from "../../../common/utilities/password-policy/password-policy.util";
import { PasswordRules } from "../../../components/auth/password-rules/password-rules.comp";
import { FirebaseAuthContainer } from "../../../common/containers/auth/firebase-auth-container.util";
import { emailRegex } from "../../../common/utilities/constants/constants";
import { AuthTemplate } from "../../../components/auth-template/auth-template.comp";
import { Loader, LoaderView } from "../../../components/loader/loader.comp";
import { validateReturnUrl } from "../../../common/utilities/auth-domain/return-url-validator.util";
import {
    parseReturnUrl,
    constructCallbackUrl
} from "../../../common/utilities/auth-domain/auth-url.util";

const AuthDomainSignUp = () => {
    // Clear all storage when navigating to registration page.
    // Preserve the tenant origin: it is written during render (below) and is required
    // by FirebaseAuthContainer to resolve the Firebase tenant before OAuth. Clearing it
    // here would race the tenant fetch and produce a default-pool (tenant-less) user.
    useEffect(() => {
        const preservedTenantOrigin = sessionStorage.getItem("auth_tenant_origin");
        sessionStorage.clear();
        localStorage.clear();
        if (preservedTenantOrigin) {
            sessionStorage.setItem("auth_tenant_origin", preservedTenantOrigin);
        }
    }, []);

    const emailMask = new RegExp(process.env.REACT_APP_EMAIL_REGEX ?? emailRegex, "g");
    const [searchParams] = useSearchParams();

    const returnUrl = parseReturnUrl(searchParams);
    const invite = searchParams.get("invite");
    const tenantOrigin = searchParams.get("tenantOrigin");

    // Store tenantOrigin immediately when available to avoid race condition with FirebaseAuthContainer
    // This must happen before FirebaseAuthContainer.useContainer() is called
    // Always update if tenantOrigin is provided in URL
    if (tenantOrigin) {
        sessionStorage.setItem("auth_tenant_origin", tenantOrigin);
    }

    const {
        signUp,
        signInWithGoogle,
        signInWithMicrosoft,
        signInWithSso,
        providerLoading,
        isTrial,
        sendVerification,
        sendVerificationWaitingTime
    } = FirebaseAuthContainer.useContainer();

    // Check if tenant is initialized (isTrial will be undefined until tenant loads)
    const tenantReady = isTrial !== undefined;

    // Check if we're in an OAuth popup (opened by Firebase)
    // OAuth popup will have window.opener but no returnUrl (Firebase redirects without preserving params)
    const isOAuthPopup = !!window.opener && !returnUrl;

    // Build login link preserving all params
    const buildLoginLink = () => {
        const params = new URLSearchParams();
        if (returnUrl) params.set("returnUrl", returnUrl);
        if (tenantOrigin) params.set("tenantOrigin", tenantOrigin);
        if (invite) params.set("invite", invite);
        return `/login?${params.toString()}`;
    };
    const loginLink = buildLoginLink();

    const [login, setLogin] = useState<string>("");
    const [password, setPassword] = useState<string>("");
    const [confirmPassword, setConfirmPassword] = useState<string>("");
    const [passwordType, setPasswordType] = useState<string>("password");
    const [confirmPasswordType, setConfirmPasswordType] = useState<string>("password");
    const [errorMessage, setErrorMessage] = useState<string>("");
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isInvalid, setIsInvalid] = useState<boolean>(false);
    const [validationError, setValidationError] = useState<string>("");
    /** Set once a self-registration (no invite) created the Firebase account. */
    const [selfRegistered, setSelfRegistered] = useState<string>("");
    /** Outcome of the last "Send Again" on the check-your-email screen. */
    const [resendNote, setResendNote] = useState<string>("");
    const errorId = useId();
    const rulesId = useId();
    const authOptions = useTenantAuthOptions(tenantOrigin);
    const passwordPolicy = authOptions?.passwordPolicy ?? null;
    // Which methods this tenant offers (plan Phase 1/4) — the same gating as
    // the login page; everything hidden until known.
    const methods = authOptions?.methods ?? {
        password: false,
        google: false,
        microsoft: false,
        sso: false
    };
    const ssoProviders = authOptions?.methods.sso ? authOptions.ssoProviders : [];
    const hasFederated = methods.google || methods.microsoft || ssoProviders.length > 0;

    // Validate return URL on mount and store in sessionStorage for OAuth flow
    useEffect(() => {
        // Skip validation if we're in an OAuth popup - Firebase handles this
        if (isOAuthPopup) {
            return;
        }

        if (!returnUrl) {
            // Check if returnUrl is in sessionStorage (from OAuth redirect)
            const storedReturnUrl = sessionStorage.getItem("auth_return_url");
            if (!storedReturnUrl) {
                setValidationError("Missing return URL. Please navigate from your tenant domain.");
                return;
            }
        } else {
            // Store returnUrl in sessionStorage for OAuth popup flow
            sessionStorage.setItem("auth_return_url", returnUrl);

            // tenantOrigin is already stored at component initialization to avoid race condition

            const validation = validateReturnUrl(returnUrl, window.location.hostname);
            if (!validation.isValid) {
                setValidationError(validation.error || "Invalid return URL");
            }
        }
    }, [returnUrl, tenantOrigin, isOAuthPopup]);

    const handleLoginChange = (evt: ChangeEvent<HTMLInputElement>) => {
        const val = evt.target?.value;
        setLogin(val ?? "");
        setErrorMessage("");
        setIsInvalid(false);
    };

    const handlePasswordChange = (evt: ChangeEvent<HTMLInputElement>) => {
        const val = evt.target?.value;
        setPassword(val ?? "");
        setErrorMessage("");
        setIsInvalid(false);
    };

    const handleConfirmPasswordChange = (evt: ChangeEvent<HTMLInputElement>) => {
        const val = evt.target?.value;
        setConfirmPassword(val ?? "");
        setErrorMessage("");
        setIsInvalid(false);
    };

    const handlePasswordEyeClick = () => {
        setPasswordType(prevValue => (prevValue === "text" ? "password" : "text"));
    };

    const handleConfirmPasswordEyeClick = () => {
        setConfirmPasswordType(prevValue => (prevValue === "text" ? "password" : "text"));
    };

    // Same backend send as the sign-up itself (the Firebase session from
    // createUserWithEmailAndPassword is still held), with the container's
    // 60s countdown between sends.
    const handleSendAgainClick = async () => {
        setResendNote("");
        try {
            await sendVerification();
            setResendNote("Sent.");
        } catch {
            setResendNote("The email could not be sent. Please try again.");
        }
    };

    const handleSignUpClick = async () => {
        if (!login || !password || !confirmPassword || !returnUrl || validationError) return;

        if (!login.match(emailMask)) {
            setIsInvalid(true);
            setErrorMessage(AuthErrorMessage.invalidEmail);
            return;
        } else if (!passwordMeetsRules(passwordPolicy, password)) {
            setIsInvalid(true);
            setErrorMessage(AuthErrorMessage.passwordPolicy);
            return;
        } else if (password !== confirmPassword) {
            setIsInvalid(true);
            setErrorMessage(AuthErrorMessage.differentPasswords);
            return;
        } else if (!invite && !authOptions?.selfRegistration) {
            setIsInvalid(true);
            setErrorMessage(AuthErrorMessage.invalidInvite);
            return;
        }

        setIsLoading(true);

        try {
            const response = await signUp(login, password, invite);
            if (!invite) {
                // Self-registration (Phase 5, D8): the account exists in Firebase
                // only until the address is verified and the user logs in, so
                // stay here rather than hand an unknown user to the tenant.
                setSelfRegistered(response.user.email ?? login);
                setIsLoading(false);
                return;
            }
            const token = await response.user.getIdToken();
            const refreshToken = response.user.refreshToken;

            // Redirect to tenant with tokens
            const callbackUrl = constructCallbackUrl(
                returnUrl,
                { accessToken: token, refreshToken },
                true
            );

            window.location.href = callbackUrl;
        } catch (error: any) {
            switch (error.code as FirebaseErrorCode) {
                case FirebaseErrorCode.emailAlreadyInUse:
                    setIsInvalid(true);
                    setErrorMessage(AuthErrorMessage.emailAlreadyInUse);
                    break;
                case FirebaseErrorCode.weakPassword:
                    setIsInvalid(true);
                    setErrorMessage(AuthErrorMessage.weakPassword);
                    break;
                case FirebaseErrorCode.passwordPolicy:
                    setIsInvalid(true);
                    setErrorMessage(AuthErrorMessage.passwordPolicy);
                    break;
                case FirebaseErrorCode.invalidInvite:
                    setIsInvalid(true);
                    setErrorMessage(AuthErrorMessage.invalidInvite);
                    break;
                case FirebaseErrorCode.inappropriateInvite:
                    setIsInvalid(true);
                    setErrorMessage(AuthErrorMessage.inappropriateInvite);
                    break;
                case FirebaseErrorCode.domainNotAllowed:
                    setIsInvalid(true);
                    setErrorMessage(AuthErrorMessage.domainNotAllowed);
                    break;
                default:
                    setErrorMessage(AuthErrorMessage.default);
            }
            setIsLoading(false);
        }
    };

    /** Hand a federated sign-in (Google, Microsoft, SSO) back to the tenant
     *  with tokens, email, invite and Firebase UID for OAuth provisioning. */
    const completeProviderSignIn = async (response: UserCredential) => {
        const token = await response.user.getIdToken();
        const refreshToken = response.user.refreshToken;
        const userEmail = response.user.email;
        // Get returnUrl from sessionStorage or query params
        const finalReturnUrl = returnUrl || sessionStorage.getItem("auth_return_url");
        if (!finalReturnUrl) {
            setErrorMessage("Missing return URL. Please try again.");
            return;
        }
        if (!userEmail) {
            setErrorMessage("User email not found. Please try again.");
            return;
        }
        sessionStorage.removeItem("auth_return_url");
        window.location.href = constructCallbackUrl(
            finalReturnUrl,
            { accessToken: token, refreshToken },
            true,
            undefined,
            userEmail,
            invite,
            response.user.uid
        );
    };

    const handleSignInWithGoogleClick = async () => {
        if (isLoading || validationError) return;
        try {
            await completeProviderSignIn(await signInWithGoogle(invite));
        } catch (error: any) {
            providerErrorHandler(error);
        }
    };

    const handleSignInWithMicrosoftClick = async () => {
        if (isLoading || validationError) return;
        try {
            await completeProviderSignIn(await signInWithMicrosoft(invite));
        } catch (error: any) {
            providerErrorHandler(error);
        }
    };

    const handleSignInWithSsoClick = async (provider: SsoProviderOption) => {
        if (isLoading || validationError) return;
        setErrorMessage("");
        try {
            await completeProviderSignIn(await signInWithSso(provider, invite));
        } catch (error: any) {
            // The password account already exists, so this isn't a sign-up:
            // the login page has the connect-with-password step.
            if (error?.code === FirebaseErrorCode.ssoLinkRequired) {
                setErrorMessage(AuthErrorMessage.ssoLinkUseLogin);
                return;
            }
            providerErrorHandler(error);
        }
    };

    const providerErrorHandler = (error: any) => {
        switch (error.code as FirebaseErrorCode) {
            case FirebaseErrorCode.closePopup:
            case FirebaseErrorCode.cancelPopup:
                break;
            case FirebaseErrorCode.invalidInvite:
                setErrorMessage(AuthErrorMessage.invalidInvite);
                return;
            case FirebaseErrorCode.inappropriateInvite:
                setErrorMessage(AuthErrorMessage.inappropriateInvite);
                return;
            case FirebaseErrorCode.invalidEmail:
                setErrorMessage(AuthErrorMessage.invalidEmail);
                return;
            case FirebaseErrorCode.forbiddenInvite:
                setErrorMessage(AuthErrorMessage.forbiddenInvite);
                return;
            case FirebaseErrorCode.invalidProvider:
                setErrorMessage(AuthErrorMessage.invalidProvider);
                return;
            case FirebaseErrorCode.directoryNotAllowed:
                setErrorMessage(AuthErrorMessage.directoryNotAllowed);
                return;
            case FirebaseErrorCode.domainNotAllowed:
                setErrorMessage(AuthErrorMessage.domainNotAllowed);
                return;
            case FirebaseErrorCode.accessPermissions:
                setErrorMessage(AuthErrorMessage.lackPermissions);
                return;
            default:
                setErrorMessage(AuthErrorMessage.default);
        }
    };

    // If we're in an OAuth popup, show loading state (Firebase will handle communication)
    if (isOAuthPopup) {
        return (
            <AuthTemplate>
                <div className="signup">
                    <div className="signup-title">Completing authentication...</div>
                    <div className="signup-content">
                        <LoaderView show={true} />
                    </div>
                </div>
            </AuthTemplate>
        );
    }

    // Show validation error if return URL is invalid
    if (validationError) {
        return (
            <AuthTemplate>
                <div className="signup">
                    <div className="signup-title">Authentication Error</div>
                    <div className="signup-content">
                        <div
                            className="signup-message"
                            style={{ padding: "20px", textAlign: "center" }}
                        >
                            {validationError}
                        </div>
                        <div
                            style={{
                                padding: "20px",
                                textAlign: "center",
                                fontSize: "14px",
                                color: "#666"
                            }}
                        >
                            Please ensure you navigate to the registration page from your tenant
                            domain.
                        </div>
                    </div>
                </div>
            </AuthTemplate>
        );
    }

    if (selfRegistered) {
        return (
            <AuthTemplate>
                <div className="signup">
                    <div className="signup-title" role="heading" aria-level={1}>
                        Check your email
                    </div>
                    <div className="signup-registered">
                        <p className="signup-registered__text" role="status">
                            We sent a verification link to <strong>{selfRegistered}</strong>. Open
                            it, then log in to finish creating your account.
                        </p>
                        <p className="signup-registered__text">
                            Didn&apos;t receive it? Check spam or send again
                            {sendVerificationWaitingTime
                                ? ` (in ${sendVerificationWaitingTime}s)`
                                : ""}
                            .
                        </p>
                        {resendNote && (
                            <p className="signup-registered__text" role="status">
                                {resendNote}
                            </p>
                        )}
                    </div>
                    <div className="signup-action">
                        <Button
                            className="primary-button"
                            variant="text"
                            onClick={handleSendAgainClick}
                            disabled={!!sendVerificationWaitingTime}
                        >
                            Send Again
                        </Button>
                    </div>
                    <div className="signup-description">
                        <Link to={loginLink} className="signup-description__link">
                            Go to Log In
                        </Link>
                    </div>
                </div>
            </AuthTemplate>
        );
    }

    return (
        <AuthTemplate>
            <div className="signup">
                <div className="signup-title" role="heading" aria-level={1}>
                    Sign Up
                </div>
                <div className="signup-description">
                    <span className="signup-description__text">Already have an account?</span>
                    <Link to={loginLink} className="signup-description__link">
                        Log In
                    </Link>
                </div>
                <div className="signup-content">
                    {methods.password && (
                        <>
                            <div className="signup-field">
                                <input
                                    className="signup-field__input--required"
                                    type="email"
                                    aria-label="Email"
                                    autoComplete="email"
                                    required
                                    aria-invalid={errorMessage ? true : undefined}
                                    aria-describedby={errorMessage ? errorId : undefined}
                                    placeholder="Email"
                                    onChange={handleLoginChange}
                                    value={login}
                                />
                                <div className="signup-field-settings">
                                    <span
                                        className="signup-field-settings__required"
                                        aria-hidden="true"
                                    >
                                        *
                                    </span>
                                </div>
                            </div>
                            <div className="signup-field">
                                <input
                                    className="signup-field__input--eye-required"
                                    type={passwordType}
                                    aria-label="Password"
                                    autoComplete="new-password"
                                    required
                                    aria-invalid={errorMessage ? true : undefined}
                                    aria-describedby={
                                        [errorMessage ? errorId : "", password ? rulesId : ""]
                                            .filter(Boolean)
                                            .join(" ") || undefined
                                    }
                                    placeholder="Password"
                                    onChange={handlePasswordChange}
                                    value={password}
                                />
                                <div className="signup-field-settings">
                                    <button
                                        type="button"
                                        className="signup-field-settings__eye"
                                        aria-label={
                                            passwordType === "text"
                                                ? "Hide password"
                                                : "Show password"
                                        }
                                        aria-pressed={passwordType === "text"}
                                        onClick={handlePasswordEyeClick}
                                    >
                                        <SvgIcon
                                            component={
                                                passwordType === "text" ? EyeIcon : EyeOffIcon
                                            }
                                            inheritViewBox
                                            aria-hidden="true"
                                        />
                                    </button>
                                    <span
                                        className="signup-field-settings__required"
                                        aria-hidden="true"
                                    >
                                        *
                                    </span>
                                </div>
                            </div>
                            {password && (
                                <PasswordRules
                                    policy={passwordPolicy}
                                    password={password}
                                    id={rulesId}
                                />
                            )}
                            <div className="signup-field">
                                <input
                                    className="signup-field__input--eye-required"
                                    type={confirmPasswordType}
                                    aria-label="Confirm Password"
                                    autoComplete="new-password"
                                    required
                                    aria-invalid={errorMessage ? true : undefined}
                                    aria-describedby={errorMessage ? errorId : undefined}
                                    placeholder="Confirm Password"
                                    onChange={handleConfirmPasswordChange}
                                    value={confirmPassword}
                                />
                                <div className="signup-field-settings">
                                    <button
                                        type="button"
                                        className="signup-field-settings__eye"
                                        aria-label={
                                            confirmPasswordType === "text"
                                                ? "Hide password"
                                                : "Show password"
                                        }
                                        aria-pressed={confirmPasswordType === "text"}
                                        onClick={handleConfirmPasswordEyeClick}
                                    >
                                        <SvgIcon
                                            component={
                                                confirmPasswordType === "text"
                                                    ? EyeIcon
                                                    : EyeOffIcon
                                            }
                                            inheritViewBox
                                            aria-hidden="true"
                                        />
                                    </button>
                                    <span
                                        className="signup-field-settings__required"
                                        aria-hidden="true"
                                    >
                                        *
                                    </span>
                                </div>
                            </div>
                        </>
                    )}
                    <div className="signup-message">
                        <span id={errorId} role="alert">
                            {errorMessage}
                        </span>
                    </div>
                    {methods.password && hasFederated && <div className="signin-separator"></div>}
                    {hasFederated && (
                        <div className="signin-providers">
                            {methods.google && (
                                <Button
                                    className="signin-providers__button"
                                    variant="outlined"
                                    onClick={handleSignInWithGoogleClick}
                                    disabled={!tenantReady}
                                    startIcon={<SvgIcon component={GoogleIcon} inheritViewBox />}
                                >
                                    Continue with Google
                                </Button>
                            )}
                            {methods.microsoft && (
                                <Button
                                    className="signin-providers__button"
                                    variant="outlined"
                                    onClick={handleSignInWithMicrosoftClick}
                                    disabled={!tenantReady}
                                    startIcon={<SvgIcon component={MicrosoftIcon} inheritViewBox />}
                                >
                                    Continue with Microsoft
                                </Button>
                            )}
                            {ssoProviders.map(provider => (
                                <Button
                                    key={provider.id}
                                    className="signin-providers__button"
                                    variant="outlined"
                                    onClick={() => handleSignInWithSsoClick(provider)}
                                    disabled={!tenantReady}
                                    startIcon={<SvgIcon component={SsoIcon} inheritViewBox />}
                                >
                                    Continue with {provider.displayName}
                                </Button>
                            ))}
                        </div>
                    )}
                </div>
                {methods.password && (
                    <div className="signup-action">
                        <Button
                            className="primary-button"
                            variant="text"
                            onClick={handleSignUpClick}
                            disabled={
                                !login || !password || !confirmPassword || isLoading || isInvalid
                            }
                        >
                            {!isLoading ? (
                                <span>Sign up</span>
                            ) : (
                                <Loader size={24} borderWidth={4} />
                            )}
                        </Button>
                    </div>
                )}
                <LoaderView show={providerLoading || !authOptions} />
            </div>
        </AuthTemplate>
    );
};

export default AuthDomainSignUp;
